import {
  SEGMENT_FIELD_TYPES,
  SEGMENT_TYPES,
  clone,
} from "./registry/segmentRegistryCore";
import structureSegments from "./registry/structureSegments";
import commerceSegments from "./registry/commerceSegments";
import contentSegments from "./registry/contentSegments";
import closingSegments from "./registry/closingSegments";

const registry = {
  ...structureSegments,
  ...commerceSegments,
  ...contentSegments,
  ...closingSegments,
};

export { SEGMENT_FIELD_TYPES, SEGMENT_TYPES };

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

export const SEGMENT_REGISTRY = deepFreeze(registry);
export const SEGMENT_ORDER = Object.freeze([
  SEGMENT_TYPES.NAVBAR,
  SEGMENT_TYPES.HERO,
  SEGMENT_TYPES.SPOTLIGHT,
  SEGMENT_TYPES.FEATURED,
  SEGMENT_TYPES.BRANDS,
  SEGMENT_TYPES.CATEGORIES,
  SEGMENT_TYPES.ABOUT,
  SEGMENT_TYPES.HIGHLIGHTS,
  SEGMENT_TYPES.IMAGE_CAROUSEL,
  SEGMENT_TYPES.TESTIMONIALS,
  SEGMENT_TYPES.CONTACT,
  SEGMENT_TYPES.FOOTER,
  SEGMENT_TYPES.FLOATING_WHATSAPP,
]);

export function getSegmentDefinition(segmentType) {
  return SEGMENT_REGISTRY[segmentType] || null;
}



const ELEMENT_TARGET_PATH_KEYS = Object.freeze([
  "fieldPath",
  "mobileVariantPath",
  "showPath",
  "labelPath",
  "urlPath",
  "collectionPath",
  "singularPath",
  "pluralPath",
  "imagePath",
]);

const ELEMENT_TARGET_PATH_LIST_KEYS = Object.freeze([
  "extraPaths",
  "fieldPaths",
]);

function descriptorPaths(descriptor, targetKey = "") {
  const paths = new Set();
  const normalizedTargetKey = String(targetKey || "").trim();
  if (normalizedTargetKey && !normalizedTargetKey.startsWith("@")) {
    paths.add(normalizedTargetKey);
  }

  ELEMENT_TARGET_PATH_KEYS.forEach((key) => {
    const value = String(descriptor?.[key] || "").trim();
    if (value) paths.add(value);
  });
  ELEMENT_TARGET_PATH_LIST_KEYS.forEach((key) => {
    (Array.isArray(descriptor?.[key]) ? descriptor[key] : []).forEach((value) => {
      const normalized = String(value || "").trim();
      if (normalized) paths.add(normalized);
    });
  });

  return [...paths];
}

function pathCoveredByRegisteredProperty(definition, path) {
  const candidate = String(path || "").trim();
  if (!candidate) return false;
  const properties = definition?.properties || [];
  if (properties.some((property) => property.path === candidate)) return true;

  return properties.some((property) => {
    if (property.type !== SEGMENT_FIELD_TYPES.COLLECTION || !property.path) return false;
    const prefix = `${property.path}.`;
    if (!candidate.startsWith(prefix)) return false;
    const remainder = candidate.slice(prefix.length).split(".").filter(Boolean);
    if (!remainder.length || !/^\d+$/.test(remainder[0])) return false;
    if (remainder.length === 1) return true;
    const itemFields = Array.isArray(property.item?.fields) ? property.item.fields : [];
    return itemFields.some((field) => field.key === remainder[1]);
  });
}

function prefixCoveredByRegisteredProperty(definition, prefix) {
  const candidate = String(prefix || "").trim();
  if (!candidate) return true;
  return (definition?.properties || []).some((property) =>
    String(property?.path || "").startsWith(candidate)
  );
}

export function validateSegmentElementDescriptor(segmentType, descriptor, targetKey = "") {
  const definition = getSegmentDefinition(segmentType);
  if (!definition || !descriptor) return ["descriptor inválido"];

  const invalid = descriptorPaths(descriptor, targetKey)
    .filter((path) => !pathCoveredByRegisteredProperty(definition, path));
  const fieldPrefix = String(descriptor?.fieldPrefix || "").trim();
  if (fieldPrefix && !prefixCoveredByRegisteredProperty(definition, fieldPrefix)) {
    invalid.push(`${fieldPrefix}*`);
  }
  return invalid;
}

export function getSegmentContextualOwnedPaths(segmentType) {
  const definition = getSegmentDefinition(segmentType);
  if (!definition?.elementEditingReady) return [];
  return [...new Set(
    Object.entries(definition.elementTargets || {})
      .flatMap(([targetKey, descriptor]) => descriptorPaths(descriptor, targetKey))
  )];
}

export function getSegmentElementDescriptor(segmentType, elementId) {
  const definition = getSegmentDefinition(segmentType);
  const rawId = String(elementId || "").trim();
  if (!definition || !rawId) return null;
  const candidates = rawId.startsWith("@") || rawId.includes(".")
    ? [rawId]
    : [`@${rawId}`, rawId];
  const targetKey = candidates.find((candidate) => definition.elementTargets?.[candidate]);
  if (!targetKey) return null;
  return {
    ...definition.elementTargets[targetKey],
    sectionId: segmentType,
    elementId: targetKey.startsWith("@") ? targetKey.slice(1) : targetKey,
    targetKey,
  };
}

export function getSegmentElementDescriptors(segmentType) {
  const definition = getSegmentDefinition(segmentType);
  return Object.entries(definition?.elementTargets || {}).map(([targetKey, descriptor]) => ({
    ...descriptor,
    sectionId: segmentType,
    elementId: targetKey.startsWith("@") ? targetKey.slice(1) : targetKey,
    targetKey,
  }));
}

export function validateRegisteredElementDescriptors() {
  return Object.entries(SEGMENT_REGISTRY).flatMap(([segmentType, definition]) =>
    Object.entries(definition?.elementTargets || {}).flatMap(([targetKey, descriptor]) =>
      validateSegmentElementDescriptor(segmentType, descriptor, targetKey).map((path) => ({
        segmentType,
        targetKey,
        path,
      }))
    )
  );
}

export function validateRegisteredElementBindings() {
  return Object.entries(SEGMENT_REGISTRY).flatMap(([segmentType, definition]) => {
    if (definition?.elementEditingReady !== true) return [];
    const ownersByPath = new Map();

    Object.entries(definition.elementTargets || {}).forEach(([targetKey, descriptor]) => {
      descriptorPaths(descriptor, targetKey).forEach((path) => {
        if (!ownersByPath.has(path)) ownersByPath.set(path, new Set());
        ownersByPath.get(path).add(targetKey);
      });
    });

    return [...ownersByPath.entries()]
      .filter(([, owners]) => owners.size > 1)
      .map(([path, owners]) => ({
        segmentType,
        path,
        owners: [...owners],
      }));
  });
}

if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
  const invalidDescriptors = validateRegisteredElementDescriptors();
  const duplicateBindings = validateRegisteredElementBindings();
  if (invalidDescriptors.length && typeof console !== "undefined") {
    console.warn("[Landing Studio] Hay targets contextuales con paths no registrados", invalidDescriptors);
  }
  if (duplicateBindings.length && typeof console !== "undefined") {
    console.warn("[Landing Studio] Hay paths con más de un owner contextual", duplicateBindings);
  }
}

export function buildSegmentVisibilityChanges(segmentType, nextVisible) {
  const definition = getSegmentDefinition(segmentType);
  const visibilityPath = definition?.visibilityPath;
  if (!visibilityPath) return {};

  const visible = nextVisible === true;
  const changes = { [visibilityPath]: visible };

  (definition.properties || [])
    .filter(
      (property) =>
        property.type === SEGMENT_FIELD_TYPES.BOOLEAN &&
        /\.enabled$/.test(property.path || "")
    )
    .forEach((property) => {
      changes[property.path] = visible;
    });

  if (definition.requiresProducts && visible) {
    changes["sections.showProducts"] = true;
  }

  return changes;
}

export function getSegmentEditableProperties(segmentType) {
  return getSegmentDefinition(segmentType)?.properties || [];
}

export function getSegmentDefaultValues(segmentType) {
  const definition = getSegmentDefinition(segmentType);
  if (!definition) return {};
  return definition.properties.reduce((defaults, property) => {
    if (property.key === "visualStyle") {
      defaults.visualStyle = clone(property.defaultValue);
      return defaults;
    }
    defaults[property.path] = clone(property.defaultValue);
    return defaults;
  }, {});
}

export function isKnownSegmentType(segmentType) {
  return Object.prototype.hasOwnProperty.call(SEGMENT_REGISTRY, segmentType);
}

export default SEGMENT_REGISTRY;
